import { useState, useEffect, useRef } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer, PenTool, Hash
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { API, getAuthHeaders, formatMontant, formatDate } from "./helpers";
import { toast } from "sonner";
import DocumentsAidesTab from "../DocumentsAidesTab";
import DonneesMPA from "../DonneesMPA";
import SignaturePreparation from "../SignaturePreparation";
import NoteDimensionnement from "../NoteDimensionnement";
import { getRegimeAides } from "../../../utils/tenantProfile";
import { PreparerSignerTab } from "./PreparerSignerTab";
import { GeneratedDocsTab, DocThumbnailCard } from "./GeneratedDocsTab";
import { DocumentsZonesTEF } from "./DocumentsZonesTEF";
import { enteteTenant } from "../../../utils/enteteTenant";

export function DocumentsAidesTEF({ dossierId, tenantId, dossier }) {
  // Source de vérité tenantId : dossier.tenant_id (root) > prop tenantId > "tef"
  // (un dossier appartient à un tenant, indépendamment du contexte user courant)
  const effectiveTenantId = dossier?.tenant_id || tenantId || null;
  const [piecesJointes, setPiecesJointes] = useState([]);
  
  useEffect(() => {
    const loadPieces = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, {
          headers: { ...enteteTenant(effectiveTenantId), ...(token && { "Authorization": `Bearer ${token}` }) },
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : data.documents || [];
          // Uniquement les pièces pour le dossier d'aide (pas photos, pas docs déjà dans Documents)
          // EXCLURE devis_amo et contrat_amo → ils sont dans "Devis & Contrats"
          const aideTypes = ["devis", "mandat", "dal", "plan_financement", "avis_imposition", "piece_identite", "facture", "attestation_honneur", "engagement", "rapport_visite", "rib"];
          const excludeTypes = ["devis_amo", "contrat_amo", "facture_amo"];
          // Filtrer les pièces du dossier d'aide
          const filtered = items.filter(d => aideTypes.includes(d.type_document) && !excludeTypes.includes(d.type_document));
          // Reclasser: docs uploadés avec mauvais type mais qui sont des Devis/Contrat AMO TEF
          const reclassified = filtered.map(d => {
            const nom = (d.nom || d.fichier_nom || d.description || "").toLowerCase();
            // "devis" dont le nom contient "AMO" ou "Transition Energie" → Devis AMO (pas installateur)
            if (d.type_document === "devis" && (nom.includes("amo") || nom.includes("transition energie"))) {
              return { ...d, type_document: "devis_amo_uploaded" };
            }
            // "mandat" dont le nom contient "Contrat AMO" ou "Transition Energie" → Contrat AMO (pas mandat classique)
            if (d.type_document === "mandat" && (nom.includes("contrat amo") || (nom.includes("contrat") && nom.includes("transition")))) {
              return { ...d, type_document: "devis_amo_uploaded" };
            }
            return d;
          });
          setPiecesJointes(reclassified);
        }
      } catch (err) { console.error(err); }
    };
    if (dossierId) loadPieces();
  }, [dossierId]);

  const TYPE_LABELS = {
    devis: "Devis installateur",
    devis_amo_uploaded: "Devis / Contrat AMO (pièce importée)",
    mandat: "Mandat / Contrat AMO",
    dal: "DAL",
    plan_financement: "Plan de financement",
    avis_imposition: "Avis d'imposition",
    piece_identite: "Pièce d'identité",
    facture: "Facture",
    attestation_honneur: "Attestation sur l'honneur",
    engagement: "Engagement",
    rapport_visite: "Rapport de visite",
    rib: "RIB",
    autre: "Autre"
  };

  const handleDownload = async (docId, nom) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/metier/documents/${docId}/download`, {
        headers: { ...enteteTenant(effectiveTenantId), ...(token && { "Authorization": `Bearer ${token}` }) },
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = nom || 'document'; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error(err); }
  };

  return (
    <Tabs defaultValue="pieces-dossier" className="w-full">
      <TabsList className="bg-[var(--oh-bg-elevated)] border-b border-[var(--oh-border)] w-full justify-start rounded-none p-0">
        <TabsTrigger 
          value="pieces-dossier" 
          className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
          data-testid="tab-pieces-dossier"
        >
          <FileText className="w-4 h-4 mr-2" />
          Pièces du dossier ({piecesJointes.length})
        </TabsTrigger>
        <TabsTrigger 
          value="données-mpa" 
          className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
        >
          <Hash className="w-4 h-4 mr-2" />
          Données MPA
        </TabsTrigger>
        <TabsTrigger 
          value="signer" 
          className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
        >
          <PenTool className="w-4 h-4 mr-2" />
          Préparer & Signer
        </TabsTrigger>
        <TabsTrigger 
          value="documents" 
          className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
        >
          <FileText className="w-4 h-4 mr-2" />
          Documents générés
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="pieces-dossier" className="mt-4">
        {piecesJointes.length === 0 ? (
          <div className="text-center py-8" style={{ color: "var(--oh-text-muted)" }}>
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucune pièce jointe trouvée</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(
              piecesJointes.reduce((acc, doc) => {
                const type = doc.type_document || "autre";
                if (!acc[type]) acc[type] = [];
                acc[type].push(doc);
                return acc;
              }, {})
            ).map(([type, docs]) => (
              <div key={type} className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                <h4 className="text-sm font-medium mb-3" style={{ color: "var(--oh-text-muted)" }}>
                  {TYPE_LABELS[type] || type} ({docs.length})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {docs.map((doc) => {
                    const nom = doc.nom || doc.fichier_nom || "Document";
                    const ext = nom.split('.').pop().toLowerCase();
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                    const isPdf = ext === 'pdf';
                    // URL d'aperçu via l'endpoint thumbnail (images + PDFs)
                    const previewUrl = (isImage || isPdf) ? `${API}/api/metier/documents/${doc.id}/thumbnail?width=200` : null;
                    
                    return (
                      <DocThumbnailCard
                        key={doc.id}
                        doc={doc}
                        nom={nom}
                        ext={ext}
                        isImage={isImage}
                        isPdf={isPdf}
                        previewUrl={previewUrl}
                        tenantId={effectiveTenantId}
                        onDownload={() => handleDownload(doc.id, nom)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="données-mpa" className="mt-4">
        <DonneesMPA
          dossier={dossier}
          tenantId={effectiveTenantId}
          isTEF={true}
        />
      </TabsContent>
      
      <TabsContent value="signer" className="mt-4">
        <PreparerSignerTab dossierId={dossierId} dossier={dossier} tenantId={effectiveTenantId} />
      </TabsContent>
      
      <TabsContent value="documents" className="mt-4">
        <GeneratedDocsTab dossierId={dossierId} tenantId={effectiveTenantId} />
      </TabsContent>
    </Tabs>
  );
}

export function DocumentsAidesWrapper({ dossierId, tenantId, dossier, isTEF: isTEFProp, onNavigate, onUpdate }) {
  // Source de vérité tenantId : dossier.tenant_id (root) > prop tenantId
  // Évite le 404 silencieux si l'user a changé d'entreprise mais reste sur ce dossier.
  const effectiveTenantId = dossier?.tenant_id || tenantId;
  const isTEF = isTEFProp || effectiveTenantId === "tef";
  // 🔴 N°657 — LES DEUX CAPACITÉS, LUES UNE FOIS. `regime_aides` est servi par
  // `/api/platform/user/tenants` et rangé par OmniHub ; ⛔ absent ⇒ [] ⇒ on
  // ⛔ n'affiche PAS (défaut sûr, DEC-079 : inconnu ≠ activé).
  const regimes = getRegimeAides();
  const aides = regimes.length > 0;          // tef(anah) · climatpac(mpr,cee) · omv(cee)
  const cee = regimes.includes("cee");       // climatpac · omv_france
  const [devisList, setDevisList] = useState([]);
  const [selectedDevisId, setSelectedDevisId] = useState(null);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger la liste des devis du dossier
  useEffect(() => {
    const loadDevis = async () => {
      try {
        const headers = getAuthHeaders(effectiveTenantId);
        // Charger tous les devis liés au dossier via API
        const res = await fetch(`${API}/api/metier/devis?dossier_id=${dossierId}`, { headers, credentials: 'include' });
        if (res.ok) {
          const validDevis = await res.json();
          setDevisList(validDevis);

          // Sélectionner automatiquement le premier devis principal ou verrouillé
          const devisActif = validDevis.find(d => d.is_principal) || 
            validDevis.find(d => 
              ["locked", "LOCKED", "devis_signe", "DEVIS_SIGNE", "LOCKED_DEVIS", "genere", "chantier_termine", "CHANTIER_TERMINE", "dossier_depose", "DOSSIER_DEPOSE", "paye", "PAYE"].includes(d.statut)
            ) || validDevis[0];

          if (devisActif) {
            setSelectedDevisId(devisActif.id);
            setSelectedDevis(devisActif);
          }
        }
      } catch (error) {
        console.error("Erreur chargement devis:", error);
      } finally {
        setLoading(false);
      }
    };

    if (dossierId) {
      loadDevis();
    } else {
      setLoading(false);
    }
  }, [dossierId]);

  // Mettre à jour le devis sélectionné
  const handleSelectDevis = (devisId) => {
    const devis = devisList.find(d => d.id === devisId);
    setSelectedDevisId(devisId);
    setSelectedDevis(devis);
  };

  // Format statut
  const getStatutBadge = (statut) => {
    const configs = {
      draft: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]" },
      genere: { label: "Généré", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
      validation: { label: "En validation", color: "bg-amber-500" },
      locked: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]" },
      LOCKED: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]" },
      LOCKED_DEVIS: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]" },
      devis_signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
      DEVIS_SIGNE: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
      chantier_termine: { label: "Chantier terminé", color: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]" },
      CHANTIER_TERMINE: { label: "Chantier terminé", color: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]" },
      dossier_depose: { label: "Dossier déposé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" },
      DOSSIER_DEPOSE: { label: "Dossier déposé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" },
      paye: { label: "Payé", color: "bg-green-600" },
      PAYE: { label: "Payé", color: "bg-green-600" }
    };
    return configs[statut] || { label: statut, color: "bg-zinc-500/20 border border-[var(--oh-border)]" };
  };

  // Refonte Documents — LOT 1 : pour TEF, une SEULE structure unifiée en 3 zones
  // (Contrats & engagements / Documents AMO / Documents ANAH), qu'il existe ou non
  // un devis structuré. Remplace l'ancien double affichage (onglets sans devis vs
  // sélecteur de devis + onglets), source des doublons. Le parcours non-TEF (OMV/CEE)
  // reste strictement inchangé ci-dessous. Rendu indépendant du chargement des devis
  // (docs TEF scopés dossier, pas devis) → pas d'attente inutile.
  if (isTEF) {
    return <DocumentsZonesTEF dossierId={dossierId} tenantId={effectiveTenantId} dossier={dossier} onNavigate={onNavigate} onUpdate={onUpdate} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  if (devisList.length === 0) {
    if (isTEF) {
      // Pour TEF: afficher les pièces du dossier + sous-onglets même sans devis structuré
      return (
        <DocumentsAidesTEF 
          dossierId={dossierId} 
          tenantId={tenantId} 
          dossier={dossier} 
        />
      );
    }
    return (
      <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-8 text-center">
        <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
        <h3 className="text-[var(--oh-text)] font-medium mb-2">Aucun devis</h3>
        <p className="text-[var(--oh-text-muted)]">
          Ce dossier n'a pas encore de devis. Créez d'abord un devis dans l'onglet "Devis/Factures" pour accéder aux documents CEE/MPR.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sélecteur de devis
          🔴 N°657 — AVEC UN SEUL DEVIS, ⛔ IL N'Y A RIEN À CHOISIR. Le
          sélecteur (déjà auto-positionné sur ce devis) ⛔ n'apparaît qu'À
          PARTIR DE DEUX ; en dessous, on NOMME le devis courant. ⚠️ Défaut
          d'ERGONOMIE, ⛔ pas de module : la sélection elle-même ⛔ n'a pas
          changé. */}
      <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-4"
           data-testid="selecteur-devis">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-[var(--oh-accent-text)]" />
            <span className="text-[var(--oh-text)] font-medium">
              {devisList.length > 1 ? "Sélectionner un devis" : (
                selectedDevis?.numero || selectedDevis?.numero_provisoire || "Devis"
              )}
            </span>
          </div>
          {devisList.length > 1 && (
          <select
            value={selectedDevisId || ""}
            onChange={(e) => handleSelectDevis(e.target.value)}
            className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg px-4 py-2 text-[var(--oh-text)] text-sm min-w-[300px]"
          >
            {devisList.map((d) => {
              const statutInfo = getStatutBadge(d.statut);
              return (
                <option key={d.id} value={d.id}>
                  {d.numero || d.numero_provisoire || "Devis"} - {statutInfo.label} - {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(d.montant_ttc || 0)}
                </option>
              );
            })}
          </select>
          )}
        </div>
        
        {selectedDevis && (
          <div className="mt-3 pt-3 border-t border-[var(--oh-border)] flex items-center gap-4 text-sm">
            <Badge className={getStatutBadge(selectedDevis.statut).color + " text-[var(--oh-text)]"}>
              {getStatutBadge(selectedDevis.statut).label}
            </Badge>
            <span className="text-[var(--oh-text-muted)]">
              Créé le {formatDate(selectedDevis.created_at)}
            </span>
            {selectedDevis.objet && (
              <span className="text-[var(--oh-text-muted)] truncate max-w-xs">{selectedDevis.objet}</span>
            )}
          </div>
        )}
      </div>

      {/* Documents du devis sélectionné */}
      {selectedDevis ? (
        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="bg-[var(--oh-bg-elevated)] border-b border-[var(--oh-border)] w-full justify-start rounded-none p-0">
            <TabsTrigger 
              value="documents" 
              className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
            >
              <FileText className="w-4 h-4 mr-2" />
              Documents générés
            </TabsTrigger>
            {/* 🔴🔴 N°657 — « Données MPR/MPA » ⛔ n'était gaté par RIEN : seul le
                LIBELLÉ changeait. Un artisan y voyait des champs d'AIDES qu'il
                ⛔ ne demande PAS.
                🔑 LA CAPACITÉ EST LE RÉGIME D'AIDES DÉCLARÉ, ⛔ pas `dossier_amo` :
                MESURÉ — tef ['anah'], climatpac ['mpr','cee'], omv_france ['cee'],
                mario_bat ⛔ ABSENT. OMV et Climat ont des aides SANS être AMO.
                ⇒ « déclare au moins un régime » : les trois gardent l'onglet à
                l'identique, Mario ⛔ ne l'a plus. */}
            {aides && (
              <TabsTrigger
                value="données-mpr"
                className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
                data-testid="tab-donnees-mpr"
              >
                <Hash className="w-4 h-4 mr-2" />
                {isTEF ? "Données MPA" : "Données MPR"}
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="signer" 
              className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
            >
              <PenTool className="w-4 h-4 mr-2" />
              Préparer & Signer
            </TabsTrigger>
            {/* 🔴🔴 N°657 — « Note Dimensionnement » était gaté par `!isTEF` :
                UNE GARDE EN CREUX, qui s'ouvre pour TOUT tenant non-TEF — y
                compris ceux qui n'existent pas encore. ⛔ Une garde en creux
                ⛔ ne protège RIEN.
                🔑 La note de dimensionnement est un livrable CEE/THERMIQUE.
                MESURÉ : climatpac et omv_france déclarent 'cee' (ils gardent
                l'onglet), tef ⛔ ne le déclare pas (il ⛔ ne l'avait PAS non
                plus), mario_bat ⛔ non plus. */}
            {cee && (
              <TabsTrigger
                value="dimensionnement"
                className="data-[state=active]:bg-[var(--oh-bg-elevated)] rounded-none px-6 py-3"
                data-testid="tab-dimensionnement"
              >
                <Thermometer className="w-4 h-4 mr-2" />
                Note Dimensionnement
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="documents" className="mt-4 space-y-4">
            {isTEF ? (
              <GeneratedDocsTab dossierId={dossierId} tenantId={effectiveTenantId} />
            ) : (
              <DocumentsAidesTab
                devisId={selectedDevisId}
                devis={selectedDevis}
                tenantId={effectiveTenantId}
              />
            )}
          </TabsContent>
          
          {/* ⛔ Le CONTENU suit le même gate que l'onglet : un panneau monté
              sans son déclencheur resterait un module d'un autre métier
              atteignable par l'URL de l'onglet. */}
          {aides && (
            <TabsContent value="données-mpr" className="mt-4">
              <DonneesMPA
                devisId={selectedDevisId}
                devis={selectedDevis}
                dossier={dossier}
                tenantId={effectiveTenantId}
                isTEF={isTEF}
              />
            </TabsContent>
          )}
          
          <TabsContent value="signer" className="mt-4">
            <SignaturePreparation
              devisId={selectedDevisId}
              devis={selectedDevis}
              dossier={dossier}
              tenantId={effectiveTenantId}
              isTEF={isTEF}
            />
          </TabsContent>
          
          {cee && (
            <TabsContent value="dimensionnement" className="mt-4" data-testid="tab-content-dimensionnement">
              <NoteDimensionnement
                dossier={dossier}
                devis={selectedDevis}
                tenantId={effectiveTenantId}
              />
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-[var(--oh-info-text)] mx-auto mb-4" />
          <p className="text-[var(--oh-text-muted)]">Sélectionnez un devis pour voir ses documents</p>
        </div>
      )}
    </div>
  );
}
